// App root for PR-1.4. Replaces the PR-0.1 path-switch shell with
// a react-router-driven layout. WsProvider wraps the router so
// every routed page can subscribe to the ws-gateway via useWs() /
// useWsSubscription() without re-establishing the connection.
//
// The old App.css from PR-0.1 (wt-shell / wt-header / wt-empty)
// is no longer imported here — those styles moved into
// AppLayout.module.css. App.css remains on disk as an orphan
// with a deprecation comment so anyone digging through git
// history isn't surprised by a missing file.

import { Suspense, lazy } from 'react'
import { AuthShell } from './components/auth/AuthShell'
import { SiteUpdateNotice } from './components/layout/SiteUpdateNotice'
import { PixelToastProvider } from './components/ui/pixel-toast'
import { LimitExecutionNotifications } from './hooks/useLimitExecutionNotifications'
import { useServerClockCalibration } from './hooks/useServerClockCalibration'
import { PriceAlertNotifications } from './lib/priceAlertsWatcher'
import { AppRouter } from './routes/AppRouter'
import { AccountSessionProvider } from './wallet/AccountSessionContext'
import { AuthShellProvider } from './wallet/AuthShellContext'
import { PairTradeSessionHost } from './wallet/PairTradeSessionHost'
import { TradingSessionProvider } from './wallet/TradingSessionContext'
import { WalletsProvider } from './wallet/WalletsContext'
import { WsProvider } from './ws/WsContext'

// The switcher of the review variants (dev/VariantBar.tsx): a dev server only.
// The condition is a literal `false` in a build (and the test runner never
// takes it), so the bundler drops the import and the bar's chunk is never
// built — no key of a variant switch reaches production through it.
const DevVariantBar =
  import.meta.env.DEV && import.meta.env.MODE !== 'test'
    ? lazy(() => import('./dev/VariantBar').then((m) => ({ default: m.VariantBar })))
    : null

export function App() {
  useServerClockCalibration()
  return (
    <WsProvider>
      <AccountSessionProvider>
        <AuthShellProvider>
          {/* Inside WsProvider: wallet-scoped surfaces subscribe to wallet:{addr}. */}
          <WalletsProvider>
            <TradingSessionProvider>
              <PixelToastProvider>
                <SiteUpdateNotice />
                <LimitExecutionNotifications />
                <PriceAlertNotifications />
                {/* The one trade executor, above the routes: a trade in flight
                    reaches its verdict (and its toast) whatever page the user
                    goes to. The host is light; the executor loads on demand. */}
                <PairTradeSessionHost>
                  <AppRouter />
                </PairTradeSessionHost>
                <AuthShell />
                {DevVariantBar ? (
                  <Suspense fallback={null}>
                    <DevVariantBar />
                  </Suspense>
                ) : null}
              </PixelToastProvider>
            </TradingSessionProvider>
          </WalletsProvider>
        </AuthShellProvider>
      </AccountSessionProvider>
    </WsProvider>
  )
}
